(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const Base = registry.Base;
  if (!Base) return;

  const CURATED_REGION_IDS = new Set([
    'yamanashi',
    'nagano',
    'hokkaido',
    'yamagata',
    'iwate',
    'ibaraki',
    'niigata',
    'osaka',
    'okayama',
    'shimane',
  ]);

  const ISLAND_GROUPS = Object.freeze({
    hokkaido: new Set(['01']),
    shikoku: new Set(['36', '37', '38', '39']),
    kyushu: new Set(['40', '41', '42', '43', '44', '45', '46']),
  });

  const TOKYO = Object.freeze({
    name: 'Tokio', longitude: 139.69171, latitude: 35.6895,
  });

  const CAPITAL_IDS = Object.freeze({
    yamanashi: 'kofu',
    nagano: 'nagano',
    hokkaido: 'sapporo',
    yamagata: 'yamagata',
    iwate: 'morioka',
    ibaraki: 'mito',
    niigata: 'niigata',
    osaka: 'osaka',
    okayama: 'okayama',
    shimane: 'matsue',
  });

  const CAMERA_PRESETS = Object.freeze({
    desktop: Object.freeze({
      japan: Object.freeze({ width: 1200, height: 690 }),
      region: Object.freeze({ width: 560, height: 322 }),
      detail: Object.freeze({ width: 390, height: 224 }),
      focusX: 0.36,
      focusY: 0.52,
    }),
    tablet: Object.freeze({
      japan: Object.freeze({ width: 1200, height: 690 }),
      region: Object.freeze({ width: 560, height: 322 }),
      detail: Object.freeze({ width: 390, height: 224 }),
      focusX: 0.5,
      focusY: 0.52,
    }),
    mobile: Object.freeze({
      japan: Object.freeze({ width: 420, height: 310 }),
      region: Object.freeze({ width: 245, height: 181 }),
      detail: Object.freeze({ width: 176, height: 130 }),
      focusX: 0.5,
      focusY: 0.45,
    }),
  });

  class IsometricArchipelagoA21Renderer extends Base {
    mount(container, data, state = {}) {
      super.mount(container, data, state);
      this.mode = state.mode || 'shop';
      this.showCities = Boolean(state.showCities);
      this.cityMarkers = new Map();
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }

    getLayout() {
      const width = this.mapCanvas?.clientWidth || window.innerWidth;
      if (width <= 600) {
        return {
          name: 'mobile', width: 420, height: 310,
          frame: { x: 0, y: 0, width: 420, height: 300 },
          baseDepth: 3.2,
          extrusionX: 0.14,
          elevations: {
            japan: { wine: 4, shop: 8, hover: 3, selected: 16, selectedShopBonus: 2 },
            region: { wine: 2.4, shop: 4.5, hover: 2, selected: 10.5, selectedShopBonus: 1.5 },
            detail: { wine: 1.6, shop: 3, hover: 1.5, selected: 7, selectedShopBonus: 1 },
          },
        };
      }
      return {
        name: width <= 900 ? 'tablet' : 'desktop', width: 1200, height: 690,
        frame: { x: 64, y: 42, width: 1072, height: 566 },
        baseDepth: 4,
        extrusionX: 0.18,
        elevations: {
          japan: { wine: 7, shop: 13, hover: 5, selected: 24, selectedShopBonus: 3 },
          region: { wine: 3.5, shop: 6.5, hover: 3, selected: 15.5, selectedShopBonus: 2 },
          detail: { wine: 2.4, shop: 4.2, hover: 2, selected: 10.5, selectedShopBonus: 1.5 },
        },
      };
    }

    polygonArea(polygon) {
      const ring = polygon?.[0] || [];
      let area = 0;
      for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        area += (Number(ring[previous]?.[0]) * Number(ring[index]?.[1]))
          - (Number(ring[index]?.[0]) * Number(ring[previous]?.[1]));
      }
      return Math.abs(area / 2);
    }

    prepareFeatures() {
      return this.features
        .filter((feature) => String(feature?.properties?.code || '').padStart(2, '0') !== '47')
        .map((feature) => {
          const polygons = feature.geometry?.type === 'Polygon'
            ? [feature.geometry.coordinates]
            : feature.geometry?.coordinates || [];
          const mainland = [...polygons].sort((a, b) => this.polygonArea(b) - this.polygonArea(a))[0];
          return {
            ...feature,
            geometry: { type: 'Polygon', coordinates: mainland || [] },
          };
        });
    }

    islandGroup(code) {
      if (ISLAND_GROUPS.hokkaido.has(code)) return 'hokkaido';
      if (ISLAND_GROUPS.shikoku.has(code)) return 'shikoku';
      if (ISLAND_GROUPS.kyushu.has(code)) return 'kyushu';
      return 'honshu';
    }

    createCompactProjector(features, frame, layoutName) {
      const mercator = ([longitude, latitude]) => {
        const clipped = Math.max(-85, Math.min(85, Number(latitude)));
        return [
          Number(longitude) * Math.PI / 180,
          -Math.log(Math.tan((Math.PI / 4) + ((clipped * Math.PI / 180) / 2))),
        ];
      };
      const rotation = (layoutName === 'mobile' ? 9 : 13) * Math.PI / 180;
      const tilt = layoutName === 'mobile' ? 0.7 : 0.57;
      const cosine = Math.cos(rotation);
      const sine = Math.sin(rotation);
      const affine = ([x, y]) => [
        (x * cosine) - (y * sine),
        ((x * sine) + (y * cosine)) * tilt,
      ];
      const rawByGroup = new Map();
      const rawEntries = [];
      features.forEach((feature) => {
        const code = String(feature.properties?.code || '').padStart(2, '0');
        const group = this.islandGroup(code);
        this.eachCoordinate(feature, (coordinate) => {
          const point = affine(mercator(coordinate));
          rawEntries.push({ code, group, point });
          if (!rawByGroup.has(group)) rawByGroup.set(group, []);
          rawByGroup.get(group).push(point);
        });
      });
      const allXs = rawEntries.map((entry) => entry.point[0]);
      const allYs = rawEntries.map((entry) => entry.point[1]);
      const spanX = Math.max(...allXs) - Math.min(...allXs);
      const spanY = Math.max(...allYs) - Math.min(...allYs);
      const centers = new Map([...rawByGroup].map(([group, points]) => [group, {
        x: (Math.min(...points.map((point) => point[0])) + Math.max(...points.map((point) => point[0]))) / 2,
        y: (Math.min(...points.map((point) => point[1])) + Math.max(...points.map((point) => point[1]))) / 2,
      }]));
      const transforms = layoutName === 'mobile'
        ? {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.07, dx: -spanX * 0.032, dy: spanY * 0.068 },
          shikoku: { scale: 1.01, dx: spanX * 0.009, dy: -spanY * 0.014 },
          kyushu: { scale: 1, dx: spanX * 0.023, dy: -spanY * 0.022 },
        }
        : {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.09, dx: -spanX * 0.038, dy: spanY * 0.074 },
          shikoku: { scale: 1.015, dx: spanX * 0.01, dy: -spanY * 0.016 },
          kyushu: { scale: 1, dx: spanX * 0.026, dy: -spanY * 0.025 },
        };
      const transformRaw = (point, code) => {
        const group = this.islandGroup(code);
        const transform = transforms[group];
        const center = centers.get(group);
        return [
          center.x + ((point[0] - center.x) * transform.scale) + transform.dx,
          center.y + ((point[1] - center.y) * transform.scale) + transform.dy,
        ];
      };
      const transformed = rawEntries.map((entry) => transformRaw(entry.point, entry.code));
      const xs = transformed.map((point) => point[0]);
      const ys = transformed.map((point) => point[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padding = layoutName === 'mobile' ? 16 : 22;
      const content = {
        x: frame.x + padding,
        y: frame.y + padding,
        width: Math.max(1, frame.width - (padding * 2)),
        height: Math.max(1, frame.height - (padding * 2)),
      };
      const scale = Math.min(
        content.width / Math.max(maxX - minX, 0.0001),
        content.height / Math.max(maxY - minY, 0.0001)
      );
      const offsetX = content.x + ((content.width - ((maxX - minX) * scale)) / 2);
      const offsetY = content.y + ((content.height - ((maxY - minY) * scale)) / 2);
      return (coordinate, code = '') => {
        const transformedPoint = transformRaw(affine(mercator(coordinate)), String(code).padStart(2, '0'));
        return [
          offsetX + ((transformedPoint[0] - minX) * scale),
          offsetY + ((transformedPoint[1] - minY) * scale),
        ];
      };
    }

    projectorForFeature(feature) {
      const code = String(feature?.properties?.code || '').padStart(2, '0');
      return (coordinate) => this.projector(coordinate, code);
    }

    featureSidePath(feature, projector, depth) {
      const polygons = feature.geometry?.type === 'Polygon'
        ? [feature.geometry.coordinates]
        : feature.geometry?.coordinates || [];
      return polygons.map((polygon) => {
        const ring = polygon?.[0] || [];
        const projected = ring.map(projector);
        const signedArea = projected.reduce((area, point, index) => {
          const next = projected[(index + 1) % projected.length];
          return area + ((point[0] * next[1]) - (next[0] * point[1]));
        }, 0);
        const extrusionX = depth * (this.layout?.extrusionX || 0);
        return projected.slice(0, -1).map(([x1, y1], index) => {
          const [x2, y2] = projected[index + 1] || projected[0];
          const cross = ((x2 - x1) * depth) - ((y2 - y1) * extrusionX);
          const facesViewer = signedArea > 0 ? cross < 0 : cross > 0;
          if (!facesViewer || Math.abs(cross) < 0.08) return '';
          return `M${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}L${(x2 + extrusionX).toFixed(2)},${(y2 + depth).toFixed(2)}L${(x1 + extrusionX).toFixed(2)},${(y1 + depth).toFixed(2)}Z`;
        }).join('');
      }).join('');
    }

    elevationProfile() {
      return this.layout?.elevations?.[this.level] || this.layout?.elevations?.japan || {
        wine: 0, shop: 0, hover: 0, selected: 0, selectedShopBonus: 0,
      };
    }

    elevationFor(regionId, state = 'rest') {
      const profile = this.elevationProfile();
      const region = this.controller.regionById.get(regionId);
      const shop = (region?.availableWines || 0) > 0;
      const rest = shop ? profile.shop : profile.wine;
      if (state === 'hover') return rest + profile.hover;
      if (state === 'selected') return profile.selected + (shop ? profile.selectedShopBonus : 0);
      return rest;
    }

    projectedElevation(elevation) {
      return {
        x: elevation * (this.layout?.extrusionX || 0),
        y: elevation,
      };
    }

    render() {
      const layout = this.getLayout();
      this.layout = layout;
      this.layoutName = layout.name;
      this.controller.dataset.atlasLayout = layout.name;
      this.controller.dataset.mapRenderer = 'isometric-a-2-1';
      this.geometryById.clear();
      this.featureById = new Map();
      this.surfaceById = new Map();
      this.preparedFeatures = this.prepareFeatures();
      this.projector = this.createCompactProjector(this.preparedFeatures, layout.frame, layout.name);

      const svg = this.createSvg(
        layout.width,
        layout.height,
        'Isometrischer Archipel der japanischen Weinregionen',
        'Kompakte SVG-Karte der vier japanischen Hauptinseln. Zehn kuratierte Weinregionen können mit Maus, Touch, Enter oder Leertaste geöffnet werden; die übrigen Präfekturen dienen ausschließlich der Orientierung.'
      );
      svg.classList.add('wine-atlas-isometric-map');
      svg.dataset.atlasLevel = this.level;

      const defs = this.createElement('defs');
      const filter = this.createElement('filter', {
        id: `atlas-ground-shadow-${this.controller.id}`, x: '-20%', y: '-30%', width: '150%', height: '180%',
      });
      filter.appendChild(this.createElement('feGaussianBlur', { stdDeviation: layout.name === 'mobile' ? 2.6 : 4.2 }));
      defs.appendChild(filter);
      svg.appendChild(defs);

      const layers = {
        mapShadow: this.createElement('g', { class: 'isometric-layer isometric-layer--map-shadow', 'data-layer': 'map-shadow', 'aria-hidden': 'true' }),
        baseSides: this.createElement('g', { class: 'isometric-layer isometric-layer--base-sides', 'data-layer': 'base-sides', 'aria-hidden': 'true' }),
        baseTops: this.createElement('g', { class: 'isometric-layer isometric-layer--base-tops', 'data-layer': 'base-tops', 'aria-hidden': 'true' }),
        wineSides: this.createElement('g', { class: 'isometric-layer isometric-layer--wine-sides', 'data-layer': 'wine-sides', 'aria-hidden': 'true' }),
        wineTops: this.createElement('g', { class: 'isometric-layer isometric-layer--wine-tops', 'data-layer': 'wine-tops', 'aria-hidden': 'true' }),
        shopRegions: this.createElement('g', { class: 'isometric-layer isometric-layer--shop-regions', 'data-layer': 'shop-regions', 'aria-hidden': 'true' }),
        selectedRegion: this.createElement('g', { class: 'isometric-layer isometric-layer--selected-region', 'data-layer': 'selected-region', 'aria-hidden': 'true' }),
        borders: this.createElement('g', { class: 'isometric-layer isometric-layer--borders', 'data-layer': 'borders', 'aria-hidden': 'true' }),
        cities: this.createElement('g', { class: 'isometric-layer isometric-layer--cities', 'data-layer': 'cities' }),
        labels: this.createElement('g', { class: 'isometric-layer isometric-layer--labels', 'data-layer': 'labels' }),
        interface: this.createElement('g', { class: 'isometric-layer isometric-layer--interface', 'data-layer': 'interface' }),
      };
      layers.shopSides = this.createElement('g', { 'data-sub-layer': 'shop-sides' });
      layers.shopTops = this.createElement('g', { 'data-sub-layer': 'shop-tops' });
      layers.shopRegions.append(layers.shopSides, layers.shopTops);
      layers.selectedSides = this.createElement('g', { 'data-sub-layer': 'selected-sides' });
      layers.selectedTops = this.createElement('g', { 'data-sub-layer': 'selected-tops' });
      layers.selectedRegion.append(layers.selectedSides, layers.selectedTops);
      this.layers = layers;

      const entries = this.preparedFeatures.map((feature) => {
        const item = this.regionForFeature(feature);
        const interactive = Boolean(item.region && CURATED_REGION_IDS.has(item.id));
        const featureProjector = this.projectorForFeature(feature);
        const d = this.featurePath(feature, featureProjector);
        const bounds = this.featureBounds(feature, featureProjector);
        return {
          feature, item, interactive, featureProjector, d, bounds,
          depth: bounds ? bounds.y + bounds.height : 0,
        };
      }).sort((a, b) => a.depth - b.depth);

      entries.forEach(({ feature, item, interactive, featureProjector, d, bounds }) => {
        if (interactive) {
          this.featureById.set(item.id, feature);
          if (bounds) this.geometryById.set(item.id, bounds);
        }

        layers.mapShadow.appendChild(this.createElement('path', { class: 'isometric-ground-piece', d }));
        const className = `isometric-prefecture ${interactive ? 'atlas-curated atlas-profiled' : 'atlas-context'}`;
        const makeNode = (kind) => this.createElement('g', {
          class: `${className} isometric-prefecture-node isometric-prefecture-node--${kind}`,
          'data-prefecture-code': item.code,
          ...(interactive ? { 'data-atlas-region': item.id } : {}),
        });
        const makeLift = () => this.createElement('g', { class: 'isometric-prefecture-lift' });
        const baseSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--base',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const hoverSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--hover',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const selectedSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--selected',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const topPath = this.createElement('path', { class: 'isometric-prefecture-top', d, 'aria-hidden': 'true' });
        const borderPath = this.createElement('path', { class: 'isometric-prefecture-border', d, 'aria-hidden': 'true' });

        if (!interactive) {
          const sideNode = makeNode('side');
          const sideLift = makeLift();
          sideLift.appendChild(baseSide);
          sideNode.appendChild(sideLift);
          const topNode = makeNode('top');
          const topLift = makeLift();
          topLift.appendChild(topPath);
          topNode.appendChild(topLift);
          const borderNode = makeNode('border');
          const borderLift = makeLift();
          borderLift.appendChild(borderPath);
          borderNode.appendChild(borderLift);
          layers.baseSides.appendChild(sideNode);
          layers.baseTops.appendChild(topNode);
          layers.borders.appendChild(borderNode);
          return;
        }

        const available = (item.region?.availableWines || 0) > 0;
        const shadowNode = makeNode('shadow');
        shadowNode.appendChild(this.createElement('path', { class: 'isometric-prefecture-shadow', d, 'aria-hidden': 'true' }));
        const sideNode = makeNode('side');
        const sideLift = makeLift();
        sideLift.append(baseSide, hoverSide, selectedSide);
        sideNode.appendChild(sideLift);
        const topNode = makeNode('top');
        const topLift = makeLift();
        topLift.appendChild(topPath);
        topNode.appendChild(topLift);
        const borderNode = makeNode('border');
        const borderLift = makeLift();
        borderLift.appendChild(borderPath);
        borderNode.appendChild(borderLift);
        const interaction = makeNode('interaction');
        interaction.classList.add('isometric-prefecture-interaction');
        const hitLift = makeLift();
        hitLift.appendChild(this.createElement('path', { class: 'isometric-prefecture-hit', d, 'aria-hidden': 'true' }));
        const capital = item.region?.atlas?.capital;
        const markerAnchor = Number.isFinite(Number(capital?.longitude)) && Number.isFinite(Number(capital?.latitude))
          ? featureProjector([Number(capital.longitude), Number(capital.latitude)])
          : bounds
            ? [bounds.x + (bounds.width / 2), bounds.y + (bounds.height / 2)]
            : null;
        if (markerAnchor) {
          hitLift.append(
            this.createElement('circle', {
              class: 'isometric-mobile-region-touch',
              cx: markerAnchor[0].toFixed(2),
              cy: markerAnchor[1].toFixed(2),
              r: 1,
              'aria-hidden': 'true',
            }),
            this.createElement('circle', {
              class: 'isometric-mobile-region-marker',
              cx: markerAnchor[0].toFixed(2),
              cy: markerAnchor[1].toFixed(2),
              r: 3.8,
              'aria-hidden': 'true',
            })
          );
        }
        interaction.appendChild(hitLift);

        const surface = {
          id: item.id,
          available,
          feature,
          projector: featureProjector,
          shadowNode,
          sideNode,
          topNode,
          borderNode,
          interaction,
          nodes: [shadowNode, sideNode, topNode, borderNode, interaction],
          baseSide,
          hoverSide,
          selectedSide,
          homeSideLayer: available ? layers.shopSides : layers.wineSides,
          homeTopLayer: available ? layers.shopTops : layers.wineTops,
        };
        this.surfaceById.set(item.id, surface);
        layers.mapShadow.appendChild(shadowNode);
        surface.homeSideLayer.appendChild(sideNode);
        surface.homeTopLayer.appendChild(topNode);
        layers.borders.appendChild(borderNode);
        layers.interface.appendChild(interaction);
        this.bindInteractive(interaction, feature);
        this.bindSurfaceInteraction(surface);
      });

      this.placesLayer = this.renderCities(layout);
      this.regionLabel = this.renderRegionLabel();
      layers.cities.appendChild(this.placesLayer);
      layers.labels.appendChild(this.regionLabel);
      svg.append(
        layers.mapShadow,
        layers.baseSides,
        layers.baseTops,
        layers.wineSides,
        layers.wineTops,
        layers.shopRegions,
        layers.selectedRegion,
        layers.borders,
        layers.cities,
        layers.labels,
        layers.interface
      );

      this.container.replaceChildren(svg);
      this.container.setAttribute('aria-busy', 'false');
      this.svg = svg;
      this.originalViewBox = { x: 0, y: 0, width: layout.width, height: layout.height };
      this.japanViewBox = { ...this.originalViewBox };
      this.bindPointerHitResolution();
      this.syncElevations();
      this.updateRegionLabel();
      this.syncCityVisibility();
    }

    bindPointerHitResolution() {
      const resolveHit = (event) => {
        const interaction = event.target.closest?.('.isometric-prefecture-interaction[data-atlas-region]');
        if (!event.detail || !interaction) return;
        try {
          const feature = this.featureById.get(interaction.dataset.atlasRegion);
          if (!feature) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.selectFeature(feature, { focusDetail: false });
        } catch (error) {
          // The region's own click handler remains available as a safe fallback.
        }
      };
      this.svg.addEventListener('click', resolveHit, true);
      this.listeners.push([this.svg, 'click', resolveHit]);
    }

    bindSurfaceInteraction(surface) {
      const setHovered = (hovered) => {
        surface.nodes.forEach((node) => node.classList.toggle('atlas-hovered', hovered));
      };
      const setFocused = (focused) => {
        surface.nodes.forEach((node) => node.classList.toggle('atlas-focused', focused));
      };
      const onEnter = () => setHovered(true);
      const onLeave = () => setHovered(false);
      const onFocus = () => {
        setHovered(true);
        setFocused(true);
      };
      const onBlur = () => {
        setHovered(false);
        setFocused(false);
      };
      surface.interaction.addEventListener('pointerenter', onEnter);
      surface.interaction.addEventListener('pointerleave', onLeave);
      surface.interaction.addEventListener('focus', onFocus);
      surface.interaction.addEventListener('blur', onBlur);
      this.listeners.push(
        [surface.interaction, 'pointerenter', onEnter],
        [surface.interaction, 'pointerleave', onLeave],
        [surface.interaction, 'focus', onFocus],
        [surface.interaction, 'blur', onBlur]
      );
    }

    regionCode(regionId) {
      return String(this.controller.regionById.get(regionId)?.prefCode || '').padStart(2, '0');
    }

    createCityMarker(place) {
      const code = place.prefCode || this.regionCode(place.regionId);
      const [x, y] = this.projector([Number(place.point.longitude), Number(place.point.latitude)], code);
      const group = this.createElement('g', {
        class: `isometric-city isometric-city--${place.role}`,
        transform: `translate(${x.toFixed(2)} ${y.toFixed(2)})`,
        'data-city-id': place.id,
        'data-city-role': place.role,
        'data-city-name': place.name,
        'aria-hidden': 'true',
      });
      const visual = this.createElement('g', {
        class: 'isometric-city__visual',
      });
      if (place.regionId) visual.setAttribute('data-region-ids', place.regionId);
      visual.append(
        this.createElement('circle', { class: 'isometric-city-ring', cx: 0, cy: 0, r: place.role === 'tokyo' ? 3.4 : 2.8 }),
        this.createElement('circle', { class: 'isometric-city-point', cx: 0, cy: 0, r: place.role === 'tokyo' ? 2 : 1.55 })
      );
      const label = this.createElement('text', {
        class: 'isometric-city-label', x: 8, y: -7,
        'text-anchor': 'start',
      });
      label.textContent = place.name;
      visual.appendChild(label);
      group.appendChild(visual);
      this.cityMarkers.set(place.id, {
        ...place, group, visual, label, x, y, dx: 8, dy: -7, elevation: { x: 0, y: 0 },
      });
      return group;
    }

    renderCities() {
      const layer = this.createElement('g', { class: 'isometric-cities', 'aria-label': 'Geografisch projizierte Städte' });
      const places = [{ id: 'tokyo', name: TOKYO.name, point: TOKYO, prefCode: '13', role: 'tokyo' }];
      this.regions.forEach((region) => {
        const id = CAPITAL_IDS[region.id];
        if (!id || !CURATED_REGION_IDS.has(region.id) || !region.atlas?.capital) return;
        places.push({
          id,
          name: region.atlas.capital.name,
          point: region.atlas.capital,
          regionId: region.id,
          role: 'capital',
        });
      });
      places
        .filter((place) => Number.isFinite(Number(place.point?.longitude)) && Number.isFinite(Number(place.point?.latitude)))
        .forEach((place) => {
          if (!this.cityMarkers.has(place.id)) layer.appendChild(this.createCityMarker(place));
        });
      return layer;
    }

    renderRegionLabel() {
      const layer = this.createElement('g', {
        class: 'isometric-region-label',
        'aria-hidden': 'true',
      });
      layer.append(
        this.createElement('rect', {
          class: 'isometric-region-label__plaque', rx: 2,
        })
      );
      const name = this.createElement('text', {
        class: 'isometric-region-label__name',
      });
      const badge = this.createElement('text', {
        class: 'isometric-region-label__badge',
      });
      layer.append(name, badge);
      return layer;
    }

    updateRegionLabel() {
      if (!this.regionLabel) return;
      const regionId = this.selectedId === 'yamanashi' || (!this.selectedId && this.mode === 'shop')
        ? 'yamanashi'
        : '';
      const region = this.controller.regionById.get(regionId);
      const bounds = this.geometryById.get(regionId);
      const visible = Boolean(region && bounds);
      this.regionLabel.classList.toggle('is-visible', visible);
      this.regionLabel.setAttribute('aria-hidden', String(!visible));
      if (!visible) {
        this.regionLabel.removeAttribute('data-region-ids');
        this.regionLabelBox = null;
        return;
      }
      this.regionLabel.setAttribute('data-region-ids', regionId);
      this.regionLabel.classList.toggle('is-available', region.availableWines > 0);
      const compact = this.layout?.name === 'mobile';
      const hasBadge = region.availableWines > 0;
      const width = Math.max(compact ? 62 : 72, (region.name.length * (compact ? 5.2 : 6.2)) + 18);
      const height = hasBadge ? (compact ? 28 : 32) : (compact ? 18 : 21);
      const anchorX = bounds.x + (bounds.width / 2);
      const anchorY = bounds.y + (bounds.height / 2);
      const x = anchorX + (compact ? 8 : 11);
      const y = anchorY - height - (compact ? 7 : 9);
      const plaque = this.regionLabel.querySelector('.isometric-region-label__plaque');
      const name = this.regionLabel.querySelector('.isometric-region-label__name');
      const badge = this.regionLabel.querySelector('.isometric-region-label__badge');
      plaque?.setAttribute('x', x);
      plaque?.setAttribute('y', y);
      plaque?.setAttribute('width', width);
      plaque?.setAttribute('height', height);
      name?.setAttribute('x', x + 7);
      name?.setAttribute('y', y + (compact ? 11 : 13));
      if (name) name.textContent = region.name;
      badge?.setAttribute('x', x + 7);
      badge?.setAttribute('y', y + height - (compact ? 5 : 6));
      badge?.classList.toggle('is-hidden', !hasBadge);
      if (badge) badge.textContent = hasBadge ? 'IM SORTIMENT' : '';
      this.regionLabelBox = { x, y, width, height };
    }

    update(matchingIds, state = {}) {
      this.matchingIds = matchingIds || new Set();
      this.selectedId = state.selectedId || '';
      this.level = state.level || this.level;
      this.mode = state.mode || this.mode || 'shop';
      if (typeof state.showCities === 'boolean') this.showCities = state.showCities;
      const selectedSurface = this.surfaceById.get(this.selectedId);
      this.surfaceById.forEach((surface, id) => {
        const selected = id === this.selectedId;
        const region = this.controller.regionById.get(id);
        surface.nodes.forEach((node) => {
          node.classList.toggle('atlas-match', this.matchingIds.has(id));
          node.classList.toggle('atlas-available', (region?.availableWines || 0) > 0);
          node.classList.toggle('atlas-selected', selected);
          node.classList.toggle('atlas-filtered-out', !this.matchingIds.has(id));
        });
        surface.interaction.setAttribute('aria-pressed', String(selected));
        if (selected) return;
        surface.homeSideLayer.appendChild(surface.sideNode);
        surface.homeTopLayer.appendChild(surface.topNode);
        this.layers.borders.appendChild(surface.borderNode);
        this.layers.interface.appendChild(surface.interaction);
      });
      if (selectedSurface) {
        this.layers.selectedSides.appendChild(selectedSurface.sideNode);
        this.layers.selectedTops.appendChild(selectedSurface.topNode);
        this.layers.borders.appendChild(selectedSurface.borderNode);
        this.layers.interface.appendChild(selectedSurface.interaction);
      }
      this.svg?.classList.toggle('has-selection', Boolean(this.selectedId));
      this.updateRegionLabel();
      this.syncElevations();
      this.syncCityVisibility();
    }

    syncElevations() {
      if (!this.svg || !this.layout) return;
      this.surfaceById?.forEach((surface, regionId) => {
        const rest = this.elevationFor(regionId, 'rest');
        const hover = this.elevationFor(regionId, 'hover');
        const selected = this.elevationFor(regionId, 'selected');
        const restOffset = this.projectedElevation(rest);
        const hoverOffset = this.projectedElevation(hover);
        const selectedOffset = this.projectedElevation(selected);
        surface.nodes.forEach((node) => {
          node.style.setProperty('--isometric-rest-x', `${restOffset.x}px`);
          node.style.setProperty('--isometric-rest-y', `${restOffset.y}px`);
          node.style.setProperty('--isometric-hover-x', `${hoverOffset.x}px`);
          node.style.setProperty('--isometric-hover-y', `${hoverOffset.y}px`);
          node.style.setProperty('--isometric-selected-x', `${selectedOffset.x}px`);
          node.style.setProperty('--isometric-selected-y', `${selectedOffset.y}px`);
          node.style.setProperty('--isometric-shadow-x', `${Math.max(1.2, this.layout.extrusionX * 8)}px`);
        });
        surface.baseSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + rest)
        );
        surface.hoverSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + hover)
        );
        surface.selectedSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + selected)
        );
      });

      this.cityMarkers.forEach((marker) => {
        const regionId = marker.regionId;
        if (!regionId) {
          marker.elevation = { x: 0, y: 0 };
          marker.visual.style.setProperty('--isometric-place-x', '0px');
          marker.visual.style.setProperty('--isometric-place-y', '0px');
          return;
        }
        const state = regionId === this.selectedId ? 'selected' : 'rest';
        const offset = this.projectedElevation(this.elevationFor(regionId, state));
        marker.elevation = offset;
        marker.visual.style.setProperty('--isometric-place-x', `${offset.x}px`);
        marker.visual.style.setProperty('--isometric-place-y', `${offset.y}px`);
      });

      if (this.regionLabel?.dataset.regionIds) {
        const labelRegionId = this.regionLabel.dataset.regionIds;
        const labelState = labelRegionId === this.selectedId ? 'selected' : 'rest';
        const labelOffset = this.projectedElevation(this.elevationFor(labelRegionId, labelState));
        this.regionLabel.style.setProperty('--isometric-label-x', `${labelOffset.x}px`);
        this.regionLabel.style.setProperty('--isometric-label-y', `${labelOffset.y}px`);
      }
    }

    setCitiesVisible(visible) {
      this.showCities = Boolean(visible);
      this.syncElevations();
      this.syncCityVisibility();
    }

    syncCityVisibility() {
      if (!this.svg) return;
      const camera = this.cameraTarget || this.fixedCameraTarget(this.level);
      this.cityMarkers.forEach((marker) => {
        const selectedCapital = marker.role === 'capital' && marker.regionId === this.selectedId;
        let requested = false;
        if (this.level === 'japan') {
          requested = marker.id === 'tokyo' || this.showCities;
        } else {
          requested = selectedCapital || (this.showCities && marker.role === 'capital') || marker.id === 'tokyo';
        }
        const visible = requested && this.markerInsideCamera(marker, camera);
        marker.group.classList.toggle('is-visible', visible);
        marker.group.classList.toggle('is-selected-capital', selectedCapital);
        marker.group.setAttribute('aria-hidden', String(!visible));
      });
      this.applyLabelCollisions();
    }

    markerInsideCamera(marker, camera) {
      if (!camera) return false;
      const x = marker.x - (marker.elevation?.x || 0);
      const y = marker.y - (marker.elevation?.y || 0);
      const margin = this.level === 'japan' ? 5 : 3;
      return x >= camera.x + margin
        && x <= camera.x + camera.width - margin
        && y >= camera.y + margin
        && y <= camera.y + camera.height - margin;
    }

    applyLabelCollisions() {
      const occupied = [];
      const camera = this.cameraTarget || this.fixedCameraTarget(this.level);
      const fontSize = this.level === 'detail' ? 3.4 : this.level === 'region' ? 4.9 : (this.layout?.name === 'mobile' ? 7.6 : 9);
      const visible = [...this.cityMarkers.values()]
        .filter((marker) => marker.group.classList.contains('is-visible'))
        .sort((a, b) => this.cityPriority(a) - this.cityPriority(b));
      visible.forEach((marker) => {
        const width = Math.max(14, marker.name.length * fontSize * 0.59);
        const height = fontSize * 1.35;
        const anchorX = marker.x - (marker.elevation?.x || 0);
        const anchorY = marker.y - (marker.elevation?.y || 0);
        const candidates = this.labelCandidates();
        let placement = null;
        for (const [dx, dy] of candidates) {
          const box = {
            x: dx < 0 ? anchorX + dx - width : anchorX + dx,
            y: anchorY + dy - height,
            width,
            height,
          };
          if (!this.boxInsideCamera(box, camera, 2)) continue;
          if (!occupied.some((item) => this.boxesOverlap(box, item, 3))) {
            placement = { dx, dy, box };
            break;
          }
        }
        marker.label.classList.toggle('is-colliding', !placement);
        if (!placement) return;
        marker.dx = placement.dx;
        marker.dy = placement.dy;
        marker.label.setAttribute('x', placement.dx);
        marker.label.setAttribute('y', placement.dy);
        marker.label.setAttribute('text-anchor', placement.dx < 0 ? 'end' : 'start');
        occupied.push(placement.box);
      });

      if (this.regionLabelBox && this.regionLabel?.classList.contains('is-visible')) {
        const regionOffset = this.projectedElevation(this.elevationFor('yamanashi', this.selectedId === 'yamanashi' ? 'selected' : 'rest'));
        const box = {
          ...this.regionLabelBox,
          x: this.regionLabelBox.x - regionOffset.x,
          y: this.regionLabelBox.y - regionOffset.y,
        };
        const collides = occupied.some((item) => this.boxesOverlap(box, item, 4));
        this.regionLabel.classList.toggle('is-colliding', collides);
      }
    }

    boxInsideCamera(box, camera, margin = 0) {
      if (!box || !camera) return false;
      return box.x >= camera.x + margin
        && box.y >= camera.y + margin
        && box.x + box.width <= camera.x + camera.width - margin
        && box.y + box.height <= camera.y + camera.height - margin;
    }

    boxesOverlap(a, b, gap = 0) {
      return !(a.x + a.width + gap < b.x
        || b.x + b.width + gap < a.x
        || a.y + a.height + gap < b.y
        || b.y + b.height + gap < a.y);
    }

    labelCandidates() {
      if (this.level === 'detail') {
        return [[2.7, -2.3], [-2.7, -2.3], [2.7, 3.1], [-2.7, 3.1], [5.2, -3.6], [-5.2, -3.6], [5.2, 3.8], [-5.2, 3.8]];
      }
      if (this.level === 'region') {
        return [[3.8, -3.2], [-3.8, -3.2], [3.8, 4.3], [-3.8, 4.3], [7.5, -5], [-7.5, -5], [7.5, 5.2], [-7.5, 5.2]];
      }
      return [[8, -7], [-8, -7], [8, 9], [-8, 9], [16, -11], [-16, -11], [16, 12], [-16, 12]];
    }

    cityPriority(marker) {
      if (marker.id === 'tokyo') return 1;
      if (marker.role === 'capital' && marker.regionId === this.selectedId) return 2;
      return 4;
    }

    focusCoordinate() {
      const region = this.controller.regionById.get(this.selectedId);
      if (!region) return null;
      if (region.id === 'hokkaido') {
        const sapporo = region.atlas?.capital;
        const yoichi = region.atlas?.wineHub;
        if (sapporo && yoichi) return {
          longitude: (Number(sapporo.longitude) + Number(yoichi.longitude)) / 2,
          latitude: (Number(sapporo.latitude) + Number(yoichi.latitude)) / 2,
        };
      }
      if (region.atlas?.capital) return region.atlas.capital;
      return null;
    }

    fixedCameraTarget(level) {
      const presets = CAMERA_PRESETS[this.layoutName] || CAMERA_PRESETS.desktop;
      if (level === 'japan' || !this.selectedId) return { ...this.originalViewBox };
      const preset = presets[level] || presets.region;
      const focus = this.focusCoordinate();
      const code = this.regionCode(this.selectedId);
      let anchor = focus
        ? this.projector([Number(focus.longitude), Number(focus.latitude)], code)
        : null;
      if (!anchor) {
        const bounds = this.geometryById.get(this.selectedId);
        anchor = bounds ? [bounds.x + (bounds.width / 2), bounds.y + (bounds.height / 2)] : null;
      }
      if (!anchor) return { ...this.originalViewBox };
      return {
        x: anchor[0] - (preset.width * presets.focusX),
        y: anchor[1] - (preset.height * presets.focusY),
        width: preset.width,
        height: preset.height,
      };
    }

    animateViewBox(target, animate = true) {
      if (!this.svg || !target) return;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      const current = this.svg.viewBox.baseVal;
      const from = { x: current.x, y: current.y, width: current.width, height: current.height };
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const duration = animate && !reduced ? (this.layoutName === 'mobile' ? 400 : 460) : 0;
      const set = (box) => this.svg?.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
      if (!duration) {
        set(target);
        return;
      }
      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - (Math.pow(-2 * progress + 2, 2) / 2);
        set({
          x: from.x + ((target.x - from.x) * eased),
          y: from.y + ((target.y - from.y) * eased),
          width: from.width + ((target.width - from.width) * eased),
          height: from.height + ((target.height - from.height) * eased),
        });
        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const target = this.fixedCameraTarget(this.level);
      this.cameraTarget = target;
      this.svg?.setAttribute('data-atlas-level', this.level);
      this.controller.dataset.cameraPreset = this.level;
      this.controller.dataset.cameraWidth = String(target.width);
      this.updateRegionLabel();
      this.syncElevations();
      this.syncCityVisibility();
      this.animateViewBox(target, options.animate !== false);
    }

    resize() {
      const next = this.getLayout().name;
      if (next === this.layoutName) return;
      const container = this.container;
      const data = this.data;
      const state = {
        selectedId: this.selectedId,
        level: this.level,
        matchingIds: this.matchingIds,
        mode: this.mode,
        showCities: this.showCities,
      };
      this.destroy();
      super.mount(container, data, state);
      this.cityMarkers = new Map();
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }
  }

  registry['isometric-a-2-1'] = IsometricArchipelagoA21Renderer;
})();
